import React, { FunctionComponent, useCallback, useEffect, useMemo, useState } from 'react'
import { Outlet, useLocation, useNavigate, useOutletContext } from 'react-router-dom'

import styled from 'styled-components/native'

import { useFlags } from '@lyrahealth-inc/shared-app-logic/src/features/flags/useFlags'
import { BackButtonV2 } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/backButton/BackButtonV2'
import { HeaderText } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/headerText/HeaderText'
import { PrimaryButton } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/primaryButton/PrimaryButton'
import { TextButton } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/textButton/TextButton'
import { Size as HeaderTextSize } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/title/Title'
import { ThemeType } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/themes/ThemeProvider'
import { tID } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/utils'

import { CLIENTS_SESSIONS } from '../../common/constants/routingConstants'
import { useAppDispatch } from '../../data/store'
import { JOIN_BANNER_HEIGHT, useAllBannerVisibility } from '../../hooks/useAllBannerVisibility'
import { useIsProviderSessionSticky } from '../../hooks/useIsProviderSessionSticky'
import { usePreviousRoute } from '../../hooks/usePreviousRoute'
import { useVideoSessionBannerHeight } from '../../hooks/useVideoSessionBannerHeight'
import { clearSelectedAppointment } from '../clients/clientDetails/data/appointmentsSlice'
import { useSelectedClientData } from '../clients/clientDetails/data/hooks/useSelectedClientData'

/**
 * Config a schedule-session step registers so the shared full-screen header can render its primary
 * action (e.g. "Next" / "Create session") and back arrow without owning the step's logic.
 */
export type ScheduleSessionFullScreenHeaderConfig = {
  primaryLabel: string
  onPrimary: () => void
  primaryDisabled?: boolean
  primaryLoading?: boolean
  onBack?: () => void
}

type ScheduleSessionFullScreenApi = {
  setHeader: (config: ScheduleSessionFullScreenHeaderConfig) => void
  exitToOrigin: () => void
}

type OutletContext = Record<string, unknown> & { scheduleSessionFullScreen?: ScheduleSessionFullScreenApi }

/**
 * Route state the launcher passes when opening the schedule-session flow, so the header's Exit can
 * return to the page the flow was started from.
 */
export type ScheduleSessionLocationState = { scheduleSessionOrigin?: string }

const LayoutContainer = styled.View<{ videoSessionBannerHeight: number }>(({ videoSessionBannerHeight }) => ({
  marginTop: videoSessionBannerHeight,
}))

const StickyHeaderContainer = styled.View<{ theme: ThemeType; topOffset: number }>(({ topOffset }) => ({
  position: 'sticky',
  top: `${topOffset}px`,
  zIndex: 11,
  width: '100%',
}))

const HeaderBar = styled.View<{ theme: ThemeType }>(({ theme: { colors, spacing } }) => ({
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
  height: 114,
  width: '100%',
  backgroundColor: colors.backgroundPrimary,
  paddingHorizontal: spacing['24px'],
  borderBottomWidth: '1px',
  borderStyle: 'solid',
  borderBottomColor: colors.borderDefault,
}))

const HeaderTitleGroup = styled.View<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: spacing['8px'],
  flexShrink: 1,
  minWidth: 0,
}))

const HeaderActions = styled.View<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: spacing['24px'],
}))

const ExitButton = styled(TextButton)<{ theme: ThemeType }>({
  alignSelf: 'center',
})

/**
 * Read by a schedule-session step to register its header config. Returns `null` when the step is not
 * rendered inside the full-screen layout (i.e. the legacy in-chrome flow), so steps fall back to their
 * own UI.
 */
export const useScheduleSessionFullScreenHeader = (): ScheduleSessionFullScreenApi | null => {
  const context = useOutletContext<OutletContext>()
  return context?.scheduleSessionFullScreen ?? null
}

/**
 * Parent route for the new-session schedule flow. When enabled it exposes a `setHeader`
 * callback to its step routes via outlet context. When disabled it is a transparent pass-through.
 */
const ScheduleSessionFullScreenLayout: FunctionComponent = () => {
  const parentContext = useOutletContext<OutletContext>()
  const { isClientHeaderRedesignEnabled } = useFlags()
  const { data: clientDetails } = useSelectedClientData()
  const { hasJoinSessionBannerOpen } = useAllBannerVisibility()
  const videoSessionBannerHeight = useVideoSessionBannerHeight()
  const isProviderSessionSticky = useIsProviderSessionSticky()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const location = useLocation()
  const previousRoute = usePreviousRoute()

  // Page the flow was launched from, used by Exit. Prefer the explicit origin the launcher passed in
  // route state; otherwise best-effort fall back to the previous route, captured once on mount (before
  // any in-flow step navigation overwrites it).
  const [origin, setOrigin] = useState<string | undefined>(
    (location.state as ScheduleSessionLocationState | null)?.scheduleSessionOrigin,
  )
  useEffect(() => {
    setOrigin((current) => current ?? previousRoute ?? undefined)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const [headerConfig, setHeaderConfig] = useState<ScheduleSessionFullScreenHeaderConfig | null>(null)
  const setHeader = useCallback((config: ScheduleSessionFullScreenHeaderConfig) => setHeaderConfig(config), [])

  const navigateToOrigin = useCallback(() => navigate(origin ?? CLIENTS_SESSIONS.route), [navigate, origin])

  const fullScreenOutletContext = useMemo<OutletContext>(
    () => ({ ...parentContext, scheduleSessionFullScreen: { setHeader, exitToOrigin: navigateToOrigin } }),
    [parentContext, setHeader, navigateToOrigin],
  )

  if (!isClientHeaderRedesignEnabled) {
    return <Outlet context={parentContext} />
  }

  const topOffset =
    (hasJoinSessionBannerOpen && isProviderSessionSticky ? JOIN_BANNER_HEIGHT : 0) + videoSessionBannerHeight

  const onExit = () => {
    dispatch(clearSelectedAppointment())
    navigateToOrigin()
  }

  return (
    <LayoutContainer
      testID={tID('ScheduleSessionFullScreenLayout')}
      videoSessionBannerHeight={videoSessionBannerHeight}
    >
      <StickyHeaderContainer topOffset={topOffset}>
        <HeaderBar testID={tID('ScheduleSessionHeader')}>
          <HeaderTitleGroup>
            {headerConfig?.onBack && (
              <BackButtonV2
                onPress={headerConfig.onBack}
                testID={tID('ScheduleSessionHeader-back')}
                style={{ padding: 8 }}
              />
            )}
            <HeaderText text={`Schedule session for ${clientDetails?.first_name ?? ''}`} size={HeaderTextSize.LARGE} />
          </HeaderTitleGroup>
          <HeaderActions>
            <ExitButton text='Exit' onPress={onExit} testID={tID('ScheduleSessionHeader-exit')} />
            <PrimaryButton
              text={headerConfig?.primaryLabel ?? ''}
              disabled={headerConfig ? (headerConfig.primaryDisabled ?? false) : true}
              loading={headerConfig?.primaryLoading}
              onPress={() => headerConfig?.onPrimary?.()}
              testID={tID('ScheduleSession-primary-button')}
            />
          </HeaderActions>
        </HeaderBar>
      </StickyHeaderContainer>
      <Outlet context={fullScreenOutletContext} />
    </LayoutContainer>
  )
}

export default ScheduleSessionFullScreenLayout
